import { MediaLoader } from './MediaLoader.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Job } from '@volt/shared';
import {
  getVideoModelAllowlistForMode,
  pickCompatibleVideoModelForMode,
  VIDEO_MODEL_IDS,
  getVideoDurationLimit,
  getVideoModelSpec,
  isVideoModelAllowedForMode,
  isVideoModelInAllowlist,
  normalizeVideoJobOptions,
  validateVideoInput,
  type VideoAudioSourceMode,
  type VideoContentMode,
  type VideoFileLike,
  type VideoLibraryAssetReference,
  type VideoMode,
  type VideoModelSpec,
  type VideoOrientation,
  type VideoUploadField,
  type VideoUploadSpec,
  type VideoValidationError,
} from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import type { TranslationKey } from '../i18n/index.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useJobs } from '../hooks/useJobs.js';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel.js';
import { prefs } from '../lib/storage.js';
import { describeJobError } from '../lib/jobsPolling.js';
import { dataUrlToBase64, readFileAsDataUrl } from '../lib/composerAttachments.js';
import { getExtensionFromUrl, isVideoUrl, jobPreviewUrl } from '../lib/download.js';
import { buildJobTiles } from '../lib/jobTiles.js';
import { buildVeoLibraryAssets } from '../lib/videoLibrary.js';
import { lockBodyScroll } from '../lib/bodyScrollLock.js';
import { useGeneratedLibrary } from '../hooks/useGeneratedLibrary.js';
import { kindsFromAccept, type LibraryKind } from '../lib/generatedLibrary.js';
import { ModelPicker } from './ModelPicker.js';
import { LibraryPickerModal } from './LibraryPickerModal.js';
import { Icon } from './Icon.js';
import { JobActionsMenu } from './JobActionsMenu.js';
import { ImageLightbox } from './MediaLightbox.js';

const STATUS_KEYS: Record<string, TranslationKey> = {
  pending: 'job.pending',
  processing: 'job.processing',
  done: 'job.done',
  failed: 'job.failed',
  cancelled: 'job.cancelled',
};

const VIDEO_MODE_KEYS: Record<VideoMode, TranslationKey> = {
  create: 'video.modeCreate',
  edit: 'video.modeEdit',
  extend: 'video.modeExtend',
  'motion-control': 'video.modeMotionControl',
};

const UPLOAD_FIELD_KEYS: Record<VideoUploadField, TranslationKey> = {
  images: 'video.fieldImages',
  referenceImages: 'video.fieldReferenceImages',
  firstFrameImage: 'video.fieldFirstFrameImage',
  lastFrameImage: 'video.fieldLastFrameImage',
  mainVideo: 'video.fieldMainVideo',
  clips: 'video.fieldClips',
  audio: 'video.fieldAudio',
  inputVideo: 'video.fieldInputVideo',
  motionVideo: 'video.fieldMotionVideo',
  characterImages: 'video.fieldCharacterImages',
};
const LIBRARY_VIDEO_FIELD_KEY: TranslationKey = 'video.fieldLibraryVideo';

const CONTENT_MODE_KEYS: Record<VideoContentMode, TranslationKey> = {
  'grok-first-frame': 'video.contentModeGrokFirstFrame',
  'grok-images': 'video.contentModeGrokImages',
  'veo-images': 'video.contentModeVeoImages',
  'veo-frames': 'video.contentModeVeoFrames',
  'minimax-multimodal': 'video.contentModeMinimaxMultimodal',
  'minimax-frames': 'video.contentModeMinimaxFrames',
  'seedance-multimodal': 'video.contentModeSeedanceMultimodal',
  'seedance-frames': 'video.contentModeSeedanceFrames',
  'gemini-references': 'video.contentModeGeminiReferences',
  'gemini-frames': 'video.contentModeGeminiFrames',
  'wan-multimodal': 'video.contentModeWanMultimodal',
  'wan-frames': 'video.contentModeWanFrames',
  'wan-reference': 'video.contentModeWanReference',
  'wan-image-edit': 'video.contentModeWanImageEdit',
  'wan-video-edit': 'video.contentModeWanVideoEdit',
  'wan-audio-input': 'video.contentModeWanAudioInput',
  'wan-first-frame': 'video.contentModeWanFirstFrame',
};

const VIDEO_MODEL_PREF_KEY = 'lastModelVideo';

type VideoOptionKey =
  | 'ratio'
  | 'resolution'
  | 'duration'
  | 'orientation'
  | 'contentMode'
  | 'audioSourceMode'
  | 'libraryVideo';

const VIDEO_OPTION_MENU_IDS: Record<VideoOptionKey, string> = {
  ratio: 'video-option-menu-ratio',
  resolution: 'video-option-menu-resolution',
  duration: 'video-option-menu-duration',
  orientation: 'video-option-menu-orientation',
  contentMode: 'video-option-menu-content-mode',
  audioSourceMode: 'video-option-menu-audio-source-mode',
  libraryVideo: 'video-option-menu-library-video',
};

interface VideoOptionMenuPortalProps {
  menuId: string;
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  label: string;
  options: Array<{
    key: string;
    label: string;
    selected: boolean;
    onSelect: () => void;
  }>;
  onClose: () => void;
}

function VideoOptionMenuPortal({
  menuId,
  open,
  anchorEl,
  label,
  options,
  onClose,
}: VideoOptionMenuPortalProps) {
  const menuRef = useRef<HTMLUListElement>(null);
  const [style, setStyle] = useState<{ top: number; left: number; minWidth: number }>({
    top: 0,
    left: 0,
    minWidth: 160,
  });

  useLayoutEffect(() => {
    if (!open || !anchorEl) return;

    const gap = 6;
    const viewportPadding = 8;
    const updatePosition = () => {
      const anchorRect = anchorEl.getBoundingClientRect();
      const menuRect = menuRef.current?.getBoundingClientRect();
      const menuHeight = menuRect?.height ?? 220;
      const menuWidth = Math.max(anchorRect.width, menuRect?.width ?? 160);
      const maxTop = Math.max(viewportPadding, window.innerHeight - menuHeight - viewportPadding);
      const canOpenUp = anchorRect.bottom + gap + menuHeight > window.innerHeight - viewportPadding
        && anchorRect.top - gap - menuHeight >= viewportPadding;
      const nextTop = canOpenUp
        ? Math.max(viewportPadding, anchorRect.top - menuHeight - gap)
        : Math.min(maxTop, anchorRect.bottom + gap);
      const desiredLeft = anchorRect.left;
      const nextLeft = Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(viewportPadding, desiredLeft),
      );

      setStyle({ top: nextTop, left: nextLeft, minWidth: anchorRect.width });
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target) || anchorEl.contains(target)) return;
      onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        anchorEl.focus();
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorEl, onClose, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const optionNodes = menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    const selectedNode = menuRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
    (selectedNode ?? optionNodes?.[0])?.focus();
  }, [open]);

  if (!open || !anchorEl) return null;

  return createPortal(
    <ul
      id={menuId}
      ref={menuRef}
      className="custom-select-menu image-option-menu image-option-menu-portal"
      role="listbox"
      aria-label={label}
      style={{ top: style.top, left: style.left, minWidth: style.minWidth, right: 'auto', width: 'max-content' }}
    >
      {options.map((option) => (
        <li
          key={option.key}
          role="option"
          aria-selected={option.selected}
          tabIndex={option.selected ? 0 : -1}
          className={`custom-select-option${option.selected ? ' is-selected' : ''}`}
          onClick={option.onSelect}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              option.onSelect();
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const nodes = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
              const currentIndex = nodes.indexOf(event.currentTarget as HTMLElement);
              if (currentIndex === -1 || nodes.length === 0) return;
              const nextIndex =
                event.key === 'ArrowDown'
                  ? (currentIndex + 1) % nodes.length
                  : (currentIndex - 1 + nodes.length) % nodes.length;
              nodes[nextIndex]?.focus();
            }
          }}
        >
          {option.label}
        </li>
      ))}
    </ul>,
    document.body,
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function formatDuration(seconds: number): string {
  return `${Math.round(seconds * 10) / 10} s`;
}

function defaultDuration(spec: VideoModelSpec): number {
  if (spec.duration.kind === 'range') return spec.duration.defaultValue;
  if (spec.duration.kind === 'options') return spec.duration.defaultValue;
  if (spec.duration.kind === 'orientation-range') return spec.duration.defaultValue;
  return 0;
}

const ALL_LIBRARY_KINDS = ['image', 'video', 'audio'] as const;

/** Which kinds of generated content an upload slot can take, judged by its accepted types. */
function uploadKinds(upload: Pick<VideoUploadSpec, 'mimePrefixes' | 'extensions'>): LibraryKind[] {
  return kindsFromAccept([...upload.mimePrefixes, ...upload.extensions]);
}

function buildAccept(upload: Pick<VideoUploadSpec, 'mimePrefixes' | 'extensions'> | undefined): string {
  if (!upload) return '';
  return [...upload.mimePrefixes, ...upload.extensions.map((ext) => `.${ext}`)].join(',');
}

function buildUploadHelper(
  t: ReturnType<typeof useI18n>['t'],
  spec: VideoUploadSpec,
): string {
  const parts: string[] = [];
  parts.push(t(spec.required ? 'video.helperRequired' : 'video.helperOptional'));
  if (spec.maxFiles !== undefined) {
    parts.push(
      spec.minFiles === spec.maxFiles
        ? t('video.helperExactFiles', { count: String(spec.maxFiles) })
        : t('video.helperFileRange', { min: String(spec.minFiles), max: String(spec.maxFiles) }),
    );
  } else if (spec.minFiles > 0) {
    parts.push(t('video.helperMinFiles', { min: String(spec.minFiles) }));
  }
  parts.push(
    t('video.helperFormats', { formats: spec.extensions.map((ext) => ext.toUpperCase()).join(', ') }),
  );
  if (spec.maxBytes !== undefined) {
    parts.push(t('video.helperMaxEach', { max: formatBytes(spec.maxBytes) }));
  }
  if (spec.minDurationSeconds !== undefined || spec.maxDurationSeconds !== undefined) {
    const min = spec.minDurationSeconds ?? 0;
    const max = spec.maxDurationSeconds ?? '∞';
    parts.push(t('video.helperDuration', { min: String(min), max: String(max) }));
  }
  if (spec.totalDurationMaxSeconds !== undefined) {
    parts.push(t('video.helperTotalDuration', { max: String(spec.totalDurationMaxSeconds) }));
  }
  if (spec.maxResolutionLabel) parts.push(t('video.helperMaxResolution', { value: spec.maxResolutionLabel }));
  return parts.join(' ');
}

function cleanupMediaElement(media: HTMLMediaElement, objectUrl?: string): void {
  media.onloadedmetadata = null;
  media.onerror = null;
  media.removeAttribute('src');
  media.load();
  if (objectUrl) URL.revokeObjectURL(objectUrl);
}

async function readMediaDurationSeconds(file: File, tag: 'video' | 'audio'): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const media = document.createElement(tag);
    media.preload = 'metadata';
    media.onloadedmetadata = () => {
      const duration = Number.isFinite(media.duration) ? media.duration : undefined;
      cleanupMediaElement(media, url);
      resolve(duration);
    };
    media.onerror = () => {
      cleanupMediaElement(media, url);
      resolve(undefined);
    };
    media.src = url;
  });
}

async function readRemoteMediaDurationSeconds(url: string, tag: 'video' | 'audio'): Promise<number | undefined> {
  return new Promise((resolve) => {
    const media = document.createElement(tag);
    media.preload = 'metadata';
    media.onloadedmetadata = () => {
      const duration = Number.isFinite(media.duration) ? media.duration : undefined;
      cleanupMediaElement(media);
      resolve(duration);
    };
    media.onerror = () => {
      cleanupMediaElement(media);
      resolve(undefined);
    };
    media.src = url;
  });
}

async function toVideoFileLike(file: File): Promise<VideoFileLike> {
  let durationSeconds: number | undefined;
  if (file.type.startsWith('video/')) {
    durationSeconds = await readMediaDurationSeconds(file, 'video');
  } else if (file.type.startsWith('audio/')) {
    durationSeconds = await readMediaDurationSeconds(file, 'audio');
  }
  return {
    name: file.name,
    mime: file.type,
    size: file.size,
    durationSeconds,
  };
}

function activeUploadFields(spec: VideoModelSpec, contentMode: string): VideoUploadField[] {
  if (spec.contentModeUploadFields && spec.contentModes) {
    const fromMode = spec.contentModeUploadFields[contentMode as VideoContentMode];
    if (fromMode) return [...fromMode];
  }
  return Object.keys(spec.uploads) as VideoUploadField[];
}

function isUploadField(field: VideoValidationError['field']): field is VideoUploadField {
  return field in UPLOAD_FIELD_KEYS;
}

function supportsAudioControls(audio: VideoModelSpec['audio'] | undefined): audio is Extract<VideoModelSpec['audio'], { supported: true }> {
  return audio?.supported === true;
}

function errorsToMap(
  t: ReturnType<typeof useI18n>['t'],
  errors: VideoValidationError[],
  spec: VideoModelSpec,
): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  for (const error of errors) {
    const label =
      error.field === 'libraryVideo'
        ? t(LIBRARY_VIDEO_FIELD_KEY)
        : isUploadField(error.field)
          ? t(UPLOAD_FIELD_KEYS[error.field]!)
          : t('video.fieldGeneric');
    let message: string;
    if (error.type === 'required') {
      message = t('video.validationRequired', { field: label });
    } else if (error.type === 'count') {
      message = t('video.validationCount', {
        field: label,
        max: String(error.maxFiles ?? (isUploadField(error.field) ? spec.uploads[error.field]?.maxFiles : 0) ?? 0),
      });
    } else if (error.type === 'format') {
      message = t('video.validationFormat', {
        field: label,
        name: error.fileName ?? '',
        mime: error.mime ?? '',
      });
    } else if (error.type === 'size') {
      message = t('video.validationSize', {
        field: label,
        name: error.fileName ?? '',
        max: formatBytes(error.maxBytes ?? 0),
      });
    } else if (error.type === 'duration') {
      message =
        error.field === 'libraryVideo'
          ? t('video.validationLibraryVideoMaxDuration', { max: String(error.maxDurationSeconds ?? 0) })
          : t('video.validationDuration', {
              field: label,
              name: error.fileName ?? '',
              min: String(error.minDurationSeconds ?? 0),
              max: String(error.maxDurationSeconds ?? 0),
            });
    } else if (error.type === 'totalDuration') {
      message = t('video.validationTotalDuration', {
        field: label,
        max: String(error.totalDurationMaxSeconds ?? 0),
      });
    } else if (error.type === 'totalCount') {
      message = t('video.validationTotalCount', {
        max: String(error.totalFilesMax ?? 0),
      });
    } else {
      message =
        error.field === 'libraryVideo' && error.detail === 'missingReference'
          ? t('video.validationLibraryVideoReference')
          : error.field === 'libraryVideo' && error.detail === 'missingOrigin'
            ? t('video.validationLibraryVideoOriginMissing')
            : error.field === 'libraryVideo' && error.detail === 'invalidOrigin'
              ? t('video.validationLibraryVideoOriginInvalid')
              : error.field === 'libraryVideo' && error.detail === 'missingDurationMetadata'
                ? t('video.validationLibraryVideoDurationMissing')
                : error.field === 'prompt'
          ? t('video.validationPromptMax', { max: String(spec.promptMax) })
          : t('video.validationUnsupported', { field: label });
    }
    const key = error.field;
    next[key] = [...(next[key] ?? []), message];
  }
  return next;
}

interface VideoJobGridTileProps {
  job: Job;
  resultUrl: string | null;
  resultIndex: number;
  allUrls: string[];
  onOpenLightbox: (urls: string[], index: number) => void;
  onToggleFavorite: (job: Job, isFavorite: boolean) => void;
  onRegenerate: (job: Job) => void;
  onDelete: (job: Job) => void;
  onCancel: (job: Job) => void;
}

function VideoJobGridTile({
  job,
  resultUrl,
  resultIndex,
  allUrls,
  onOpenLightbox,
  onToggleFavorite,
  onRegenerate,
  onDelete,
  onCancel,
}: VideoJobGridTileProps) {
  const { t } = useI18n();
  const status = STATUS_KEYS[job.status] ?? 'job.pending';
  const isErrored = job.status === 'failed' || job.status === 'cancelled';
  const showOverlay = !resultUrl || isErrored;
  const kind: 'video' | 'image' = resultUrl && isVideoUrl(resultUrl) ? 'video' : 'image';
  const filename = resultUrl ? `result-${resultIndex + 1}.${getExtensionFromUrl(resultUrl, kind)}` : '';

  return (
    <div className={`job-tile job-tile-${job.status}`}>
      {resultUrl ? (
        kind === 'video' ? (
          <video
            className="job-tile-video"
            src={resultUrl}
            controls
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <button
            type="button"
            className="job-tile-preview"
            onClick={() => onOpenLightbox(allUrls, resultIndex)}
            aria-label={t('job.result')}
          >
            <img
              className="job-tile-img"
              src={jobPreviewUrl(job, resultIndex, resultUrl)}
              alt=""
              loading="lazy"
            />
          </button>
        )
      ) : (
        <div className="job-tile-preview job-tile-placeholder" aria-hidden="true" />
      )}

      {showOverlay && (
        <div className={`job-tile-overlay${isErrored ? ' job-tile-overlay-error' : ''}`}>
          {isErrored ? (
            <span className="job-tile-status-text">{job.error || t(status)}</span>
          ) : (
            <>
              <MediaLoader kind="video" size={36} />
              <span className="job-tile-status-text">{t(status)}</span>
            </>
          )}
        </div>
      )}

      {job.isFavorite && (
        <span className="job-tile-favorite" role="img" aria-label={t('job.favorited')}>
          <Icon name="star" size={14} />
        </span>
      )}

      <JobActionsMenu
        job={job}
        downloadHref={resultUrl}
        downloadFilename={filename || 'video'}
        resultIndex={resultIndex}
        onToggleFavorite={onToggleFavorite}
        onRegenerate={onRegenerate}
        onDelete={onDelete}
        onCancel={onCancel}
      />
    </div>
  );
}

interface VideoJobGridProps {
  jobs: Job[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRegenerate: (job: Job) => void;
  onToggleFavorite: (job: Job, isFavorite: boolean) => void;
  onDelete: (job: Job) => void;
  onCancel: (job: Job) => void;
}

/** 3-column infinite-scroll grid of generated video tiles. */
function VideoJobGrid({
  jobs,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onRegenerate,
  onToggleFavorite,
  onDelete,
  onCancel,
}: VideoJobGridProps) {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(onLoadMore, {
    enabled: hasMore && !loading,
  });

  if (loading && jobs.length === 0) {
    return (
      <div className="jobs-loading">
        <span className="spinner" />
      </div>
    );
  }
  if (jobs.length === 0) {
    return <p className="jobs-empty">{t('job.empty')}</p>;
  }

  const tiles = buildJobTiles(jobs);

  return (
    <>
      <div className="job-grid">
        {tiles.map((tile) => (
          <VideoJobGridTile
            key={tile.key}
            job={tile.job}
            resultUrl={tile.resultUrl}
            resultIndex={tile.resultIndex}
            allUrls={tile.resultUrls}
            onOpenLightbox={(urls, index) => setLightbox({ urls, index })}
            onToggleFavorite={onToggleFavorite}
            onRegenerate={onRegenerate}
            onDelete={onDelete}
            onCancel={onCancel}
          />
        ))}
        {hasMore && <div ref={sentinelRef} className="job-grid-sentinel" aria-hidden="true" />}
      </div>
      {loadingMore && (
        <div className="jobs-loading jobs-loading-more">
          <span className="spinner" />
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

export function VideoJobScreen() {
  const { t } = useI18n();
  const { data } = useSession();
  const { toast } = useUI();
  const {
    jobs,
    loading,
    loadingMore,
    hasMore,
    generating,
    create,
    loadMore,
    toggleFavorite,
    removeJob,
    cancelJob,
  } = useJobs(true);
  const { api } = useSession();
  const [pickerAnchor, setPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(() => {
    return prefs.get(VIDEO_MODEL_PREF_KEY) ?? undefined;
  });
  const [videoMode, setVideoMode] = useState<VideoMode>('create');
  const [prompt, setPrompt] = useState('');
  const [ratio, setRatio] = useState('');
  const [resolution, setResolution] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [contentMode, setContentMode] = useState<VideoContentMode>('grok-first-frame');
  const [orientation, setOrientation] = useState<VideoOrientation>('auto');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [audioSourceMode, setAudioSourceMode] = useState<VideoAudioSourceMode>('preserve');
  const [libraryVideo, setLibraryVideo] = useState<VideoLibraryAssetReference | null>(null);
  // Everything the user generated, in any section, offered as a reference for the upload slots.
  const generatedLibrary = useGeneratedLibrary(ALL_LIBRARY_KINDS);
  const [libraryField, setLibraryField] = useState<VideoUploadField | null>(null);
  const [libraryVideoDurations, setLibraryVideoDurations] = useState<Record<string, number | null>>({});
  const [filesByField, setFilesByField] = useState<Partial<Record<VideoUploadField, File[]>>>({});
  const [fileDetailsByField, setFileDetailsByField] = useState<
    Partial<Record<VideoUploadField, VideoFileLike[]>>
  >({});
  /** Mirror of `filesByField` for handlers that run before the next render. */
  const filesByFieldRef = useRef<Partial<Record<VideoUploadField, File[]>>>({});
  /** Per-field version of the last selection: metadata of an older selection is ignored. */
  const fileVersionsRef = useRef<Partial<Record<VideoUploadField, number>>>({});
  const [uploading, setUploading] = useState(false);
  /** Attachment id per file already sent, so a retry after a failed submit does not upload again. */
  const uploadedRef = useRef(new WeakMap<File, string>());
  /** Library assets whose duration is being measured right now (each one is measured once). */
  const measuringAssetIdsRef = useRef(new Set<string>());
  const [openVideoOption, setOpenVideoOption] = useState<VideoOptionKey | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const videoOptionTriggerRefs = useRef<Record<VideoOptionKey, HTMLButtonElement | null>>({
    ratio: null,
    resolution: null,
    duration: null,
    orientation: null,
    contentMode: null,
    audioSourceMode: null,
    libraryVideo: null,
  });

  const models = useMemo(() => data?.models ?? [], [data?.models]);
  const sectionModels = useMemo(
    () =>
      models.filter((model) => {
        if (model.section !== 'video' || !model.isActive || !VIDEO_MODEL_IDS.has(model.id)) return false;
        const spec = getVideoModelSpec(model.id);
        return Boolean(spec && isVideoModelAllowedForMode(model.id, spec.mode));
      }),
    [models],
  );
  const modeModels = useMemo(
    () =>
      sectionModels.filter((model) => {
        const spec = getVideoModelSpec(model.id);
        return Boolean(spec && spec.mode === videoMode && isVideoModelAllowedForMode(model.id, videoMode));
      }),
    [sectionModels, videoMode],
  );
  const selectedModel = sectionModels.find((model) => model.id === selectedModelId);
  const selectedSpec = selectedModelId ? getVideoModelSpec(selectedModelId) : undefined;
  const selectedSpecForMode =
    selectedModel &&
    selectedSpec &&
    selectedSpec.mode === videoMode &&
    isVideoModelAllowedForMode(selectedModel.id, videoMode)
      ? selectedSpec
      : undefined;
  const audioConfig = supportsAudioControls(selectedSpecForMode?.audio) ? selectedSpecForMode.audio : undefined;
  const saveOriginalAudioOnly = audioConfig?.saveOriginalAudioOnly === true;
  const selectedFields = useMemo(
    () => (selectedSpecForMode ? activeUploadFields(selectedSpecForMode, contentMode) : []),
    [contentMode, selectedSpecForMode],
  );
  const promptMax = selectedSpecForMode?.promptMax ?? 20_000;
  const promptTooLong = prompt.length > promptMax;
  const durationLimit = selectedSpecForMode ? getVideoDurationLimit(selectedSpecForMode, orientation) : {};
  const sectionJobs = useMemo(
    () =>
      jobs.filter((job) => {
        const input = job.input as Record<string, unknown>;
        return input.section === 'video' || isVideoModelInAllowlist(job.modelId);
      }),
    [jobs],
  );
  const veoLibraryAssets = useMemo(() => buildVeoLibraryAssets(sectionJobs, sectionModels), [sectionJobs, sectionModels]);
  const modeAllowlistIds = useMemo(() => getVideoModelAllowlistForMode(videoMode), [videoMode]);
  const modeModelIdSet = useMemo(() => new Set(modeModels.map((model) => model.id)), [modeModels]);
  const missingModeModelIds = useMemo(
    () => modeAllowlistIds.filter((modelId) => !modeModelIdSet.has(modelId)),
    [modeAllowlistIds, modeModelIdSet],
  );
  const isLibraryVideoMode = Boolean(selectedSpecForMode?.libraryVideo);
  const unmeasuredVeoLibraryAssets = useMemo(
    () =>
      isLibraryVideoMode
        ? veoLibraryAssets.filter(
            (asset) =>
              typeof asset.previewUrl === 'string' &&
              asset.previewUrl.length > 0 &&
              !(asset.assetId in libraryVideoDurations),
          )
        : [],
    [isLibraryVideoMode, libraryVideoDurations, veoLibraryAssets],
  );
  const maxLibraryVideoDuration = selectedSpecForMode?.libraryVideo?.maxDurationSeconds ?? Infinity;
  const eligibleVeoLibraryAssets = useMemo(
    () =>
      veoLibraryAssets
        .map((asset) => ({
          ...asset,
          durationSeconds: libraryVideoDurations[asset.assetId] ?? null,
        }))
        .filter(
          (asset) =>
            typeof asset.durationSeconds === 'number' &&
            asset.durationSeconds > 0 &&
            asset.durationSeconds <= maxLibraryVideoDuration,
        ),
    [libraryVideoDurations, maxLibraryVideoDuration, veoLibraryAssets],
  );
  const libraryVideoMetadataPending = useMemo(
    () => unmeasuredVeoLibraryAssets.length > 0,
    [unmeasuredVeoLibraryAssets.length],
  );

  useEffect(() => {
    const availableModeModelIds = modeModels.filter((model) => model.isAvailable).map((model) => model.id);
    const allModeModelIds = modeModels.map((model) => model.id);
    const nextModelId =
      pickCompatibleVideoModelForMode(videoMode, availableModeModelIds, selectedModelId) ??
      pickCompatibleVideoModelForMode(videoMode, allModeModelIds, selectedModelId);
    if (nextModelId !== selectedModelId) setSelectedModelId(nextModelId);
  }, [modeModels, selectedModelId, videoMode]);

  useEffect(() => {
    if (!selectedSpecForMode) {
      return;
    }
    setRatio(selectedSpecForMode.ratios[0] ?? '');
    setResolution(selectedSpecForMode.resolutions[0] ?? '');
    setDurationSeconds(defaultDuration(selectedSpecForMode));
    setContentMode((selectedSpecForMode.contentModes?.[0] ?? 'grok-first-frame') as VideoContentMode);
    setOrientation('auto');
    setAudioEnabled(Boolean(selectedSpecForMode.audio.supported && !saveOriginalAudioOnly));
    setAudioSourceMode('preserve');
    setLibraryVideo(null);
    setLibraryVideoDurations({});
    setFilesByField({});
    setFileDetailsByField({});
    setOpenVideoOption(null);
    setFieldErrors({});
    setPrompt((prev) => prev.slice(0, selectedSpecForMode.promptMax));
  }, [saveOriginalAudioOnly, selectedModelId, selectedSpecForMode]);

  useEffect(() => {
    if (selectedModelId || modeModels.length > 0) return;
    setLibraryVideo(null);
    setFilesByField({});
    setFileDetailsByField({});
    setFieldErrors({});
  }, [modeModels.length, selectedModelId]);

  useEffect(() => {
    if (!audioConfig || saveOriginalAudioOnly) {
      setAudioEnabled(false);
    }
  }, [audioConfig, saveOriginalAudioOnly]);

  useEffect(() => {
    if (!isLibraryVideoMode) return;
    // Each asset is measured once: a <video> element is created per measurement, and this
    // effect re-runs whenever the job feed changes.
    const measuring = measuringAssetIdsRef.current;
    const pending = unmeasuredVeoLibraryAssets.filter((asset) => !measuring.has(asset.assetId));
    if (pending.length === 0) return;
    for (const asset of pending) measuring.add(asset.assetId);

    void Promise.all(
      pending.map(async (asset) => ({
        assetId: asset.assetId,
        durationSeconds: await readRemoteMediaDurationSeconds(asset.previewUrl!, 'video'),
      })),
    ).then((results) => {
      for (const result of results) measuring.delete(result.assetId);
      setLibraryVideoDurations((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const result of results) {
          if (result.assetId in next) continue;
          next[result.assetId] = result.durationSeconds ?? null;
          changed = true;
        }
        return changed ? next : prev;
      });
    });
  }, [isLibraryVideoMode, unmeasuredVeoLibraryAssets]);

  useEffect(() => {
    filesByFieldRef.current = filesByField;
  }, [filesByField]);

  useEffect(() => {
    if (durationLimit.max && durationSeconds > durationLimit.max) {
      setDurationSeconds(durationLimit.max);
    }
  }, [durationLimit.max, durationSeconds]);

  useEffect(() => {
    setFieldErrors((prev) => {
      if (!prev.prompt) return prev;
      const next = { ...prev };
      delete next.prompt;
      return next;
    });
  }, [prompt, promptMax]);

  useEffect(() => {
    if (!openVideoOption) return;
    return lockBodyScroll();
  }, [openVideoOption]);

  useEffect(() => {
    if (!selectedSpecForMode) return;
    const activeFields = new Set(activeUploadFields(selectedSpecForMode, contentMode));
    setFilesByField((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of Object.keys(selectedSpecForMode.uploads) as VideoUploadField[]) {
        if (!activeFields.has(field) && (next[field]?.length ?? 0) > 0) {
          next[field] = [];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setFileDetailsByField((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of Object.keys(selectedSpecForMode.uploads) as VideoUploadField[]) {
        if (!activeFields.has(field) && (next[field]?.length ?? 0) > 0) {
          next[field] = [];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setFieldErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of Object.keys(selectedSpecForMode.uploads) as VideoUploadField[]) {
        if (!activeFields.has(field) && next[field]) {
          delete next[field];
          changed = true;
        }
      }
      if (next.totalFiles) {
        delete next.totalFiles;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [contentMode, selectedSpecForMode]);

  /**
   * Adds files to a slot (file input and library picker behave the same): the newest files
   * win when the slot is full. Metadata is read asynchronously, so a later selection
   * invalidates the result of an earlier one and details always match the files shown.
   */
  const handleFilesSelected = useCallback(
    async (field: VideoUploadField, files: ArrayLike<File> | null, maxFiles = Infinity) => {
      const added = files ? Array.from(files) : [];
      const limit = Math.max(1, maxFiles);
      const nextFiles = [...(filesByFieldRef.current[field] ?? []), ...added].slice(-limit);
      filesByFieldRef.current = { ...filesByFieldRef.current, [field]: nextFiles };
      setFilesByField(filesByFieldRef.current);
      const version = (fileVersionsRef.current[field] ?? 0) + 1;
      fileVersionsRef.current[field] = version;
      const details = await Promise.all(nextFiles.map((file) => toVideoFileLike(file)));
      if (fileVersionsRef.current[field] !== version) return;
      setFileDetailsByField((prev) => ({ ...prev, [field]: details }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        delete next.totalFiles;
        return next;
      });
    },
    [],
  );

  const clearField = useCallback((field: VideoUploadField) => {
    fileVersionsRef.current[field] = (fileVersionsRef.current[field] ?? 0) + 1;
    filesByFieldRef.current = { ...filesByFieldRef.current, [field]: [] };
    setFilesByField(filesByFieldRef.current);
    setFileDetailsByField((prev) => ({ ...prev, [field]: [] }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      delete next.totalFiles;
      return next;
    });
  }, []);

  const clearLibraryVideo = useCallback(() => {
    setLibraryVideo(null);
    setFieldErrors((prev) => {
      if (!prev.libraryVideo) return prev;
      const next = { ...prev };
      delete next.libraryVideo;
      return next;
    });
  }, []);

  const toggleVideoOption = useCallback((option: VideoOptionKey) => {
    setOpenVideoOption((prev) => (prev === option ? null : option));
  }, []);

  const closeVideoOption = useCallback(() => {
    setOpenVideoOption(null);
  }, []);

  /** Uploads one selected file (once) and returns its attachment id. */
  const uploadFile = useCallback(
    async (file: File): Promise<string> => {
      const known = uploadedRef.current.get(file);
      if (known) return known;
      const dataUrl = await readFileAsDataUrl(file);
      const { attachment } = await api.uploadAttachment({
        fileName: file.name || 'file',
        mime: file.type || 'application/octet-stream',
        dataBase64: dataUrlToBase64(dataUrl),
      });
      uploadedRef.current.set(file, attachment.id);
      return attachment.id;
    },
    [api],
  );

  const handleGenerate = useCallback(async () => {
    const trimmedPrompt = prompt.trim();
    if (uploading) return;
    if (!selectedSpecForMode || !selectedModelId || ((selectedSpecForMode.promptRequired ?? true) && !trimmedPrompt)) return;
    const activeFiles = Object.fromEntries(
      selectedFields.map((field) => [field, fileDetailsByField[field] ?? []]),
    ) as Partial<Record<VideoUploadField, VideoFileLike[]>>;
    const effectiveDuration =
      selectedSpecForMode.duration.kind === 'input-video'
        ? fileDetailsByField.mainVideo?.[0]?.durationSeconds ?? fileDetailsByField.inputVideo?.[0]?.durationSeconds ?? 0
        : durationSeconds;
    const errors = validateVideoInput(selectedSpecForMode, {
      prompt: trimmedPrompt,
      ratio,
      resolution,
      durationSeconds: effectiveDuration,
      libraryVideo: selectedSpecForMode.libraryVideo ? libraryVideo : undefined,
      contentMode: selectedSpecForMode.contentModes ? contentMode : undefined,
      orientation: selectedSpecForMode.duration.kind === 'orientation-range' ? orientation : undefined,
      audioSourceMode:
        audioConfig &&
        !saveOriginalAudioOnly &&
        audioConfig.supportsSourceAudioMode &&
        audioEnabled
          ? audioSourceMode
          : undefined,
      files: activeFiles,
    });
    if (errors.length > 0) {
      setFieldErrors(errorsToMap(t, errors, selectedSpecForMode));
      return;
    }

    // The vendor needs the bytes, not the file names: every file goes up as an attachment
    // and its id travels with the metadata entry.
    const uploadedFiles: Partial<Record<VideoUploadField, VideoFileLike[]>> = {};
    setUploading(true);
    try {
      for (const field of selectedFields) {
        const files = filesByField[field] ?? [];
        const details = activeFiles[field] ?? [];
        uploadedFiles[field] = await Promise.all(
          files.map(async (file, index) => ({
            ...(details[index] ?? (await toVideoFileLike(file))),
            attachmentId: await uploadFile(file),
          })),
        );
      }
    } catch (error: unknown) {
      toast(describeJobError(error, t) || t('job.uploadFailed'), 'error');
      return;
    } finally {
      setUploading(false);
    }

    try {
      const options = normalizeVideoJobOptions(selectedSpecForMode, {
        ratio,
        resolution,
        durationSeconds: effectiveDuration,
        libraryVideo: selectedSpecForMode.libraryVideo ? libraryVideo : undefined,
        contentMode: selectedSpecForMode.contentModes ? contentMode : undefined,
        orientation: selectedSpecForMode.duration.kind === 'orientation-range' ? orientation : undefined,
        audioEnabled,
        audioSourceMode:
          audioConfig &&
          !saveOriginalAudioOnly &&
          audioConfig.supportsSourceAudioMode &&
          audioEnabled
            ? audioSourceMode
            : undefined,
        ...uploadedFiles,
      });
      await create({
        modelId: selectedModelId,
        prompt: trimmedPrompt,
        options,
      });
      setPrompt('');
      setLibraryVideo(null);
      setFilesByField({});
      setFileDetailsByField({});
      setFieldErrors({});
    } catch (error: unknown) {
      toast(describeJobError(error, t), 'error');
    }
  }, [
    audioEnabled,
    audioConfig,
    audioSourceMode,
    contentMode,
    create,
    durationSeconds,
    fileDetailsByField,
    filesByField,
    uploadFile,
    uploading,
    libraryVideo,
    orientation,
    prompt,
    ratio,
    resolution,
    selectedFields,
    selectedModelId,
    selectedSpecForMode,
    saveOriginalAudioOnly,
    t,
    toast,
  ]);

  const handleRegenerate = useCallback(
    (job: Job) => {
      const { prompt: regenPrompt, ...options } = (job.input ?? {}) as Record<string, unknown> & {
        prompt?: string;
      };
      void create({
        modelId: job.modelId,
        prompt: typeof regenPrompt === 'string' ? regenPrompt : '',
        conversationId: job.conversationId ?? undefined,
        options,
      }).catch((error: unknown) => {
        toast(describeJobError(error, t), 'error');
      });
    },
    [create, t, toast],
  );

  const handleToggleFavorite = useCallback(
    (job: Job, isFavorite: boolean) => {
      void toggleFavorite(job.id, isFavorite).catch((error: unknown) => {
        toast(describeJobError(error, t), 'error');
      });
    },
    [toggleFavorite, t, toast],
  );

  const handleDeleteJob = useCallback(
    (job: Job) => {
      void removeJob(job.id).catch((error: unknown) => {
        toast(describeJobError(error, t), 'error');
      });
    },
    [removeJob, t, toast],
  );

  const handleCancelJob = useCallback(
    (job: Job) => {
      void cancelJob(job.id).catch((error: unknown) => {
        toast(describeJobError(error, t), 'error');
      });
    },
    [cancelJob, t, toast],
  );

  const disabled =
    !selectedModelId ||
    ((selectedSpecForMode?.promptRequired ?? true) && !prompt.trim()) ||
    promptTooLong ||
    generating ||
    uploading ||
    Boolean(selectedSpecForMode?.libraryVideo?.required && !libraryVideo) ||
    selectedFields.some((field) => {
      const upload = selectedSpecForMode?.uploads[field];
      const count = fileDetailsByField[field]?.length ?? 0;
      return Boolean(upload?.required && count < upload.minFiles);
    });

  return (
    <div className="media-screen">
      <div className="media-composer video-composer">
        <div className="video-mode-row" role="tablist" aria-label={t('video.modeLabel')}>
          {(['create', 'edit', 'extend', 'motion-control'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={videoMode === mode}
              aria-pressed={videoMode === mode}
              className={`chip${videoMode === mode ? ' is-active' : ''}`}
              onClick={() => setVideoMode(mode)}
            >
              {t(VIDEO_MODE_KEYS[mode]!)}
            </button>
          ))}
        </div>

        <div className="composer-model-row">
          <button
            type="button"
            className="composer-model-btn"
            disabled={modeModels.length === 0}
            onClick={(event) => {
              closeVideoOption();
              const rect = event.currentTarget.getBoundingClientRect();
              setPickerAnchor({ x: rect.left, y: rect.bottom + 8 });
            }}
          >
            <Icon name="settings" size={14} />
            <span>{selectedModel?.displayName ?? t('composer.selectModel')}</span>
            <Icon name="chevronDown" size={14} />
          </button>
        </div>
        {modeModels.length === 0 && (
          <p className="video-helper" role="status">
            {missingModeModelIds.length > 0
              ? t('video.modeModelListMissing', { ids: missingModeModelIds.join(', ') })
              : t('video.modeModelListEmpty')}
          </p>
        )}

        {selectedSpecForMode && (
          <>
            <div className="video-form-grid">
              <label className="video-field">
                <span className="video-label">{t('video.ratio')}</span>
                <div className={`custom-select image-option-select${openVideoOption === 'ratio' ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="custom-select-trigger image-option-trigger"
                    ref={(node) => {
                      videoOptionTriggerRefs.current.ratio = node;
                    }}
                    onClick={() => toggleVideoOption('ratio')}
                    aria-label={t('video.ratio')}
                    aria-haspopup="listbox"
                    aria-expanded={openVideoOption === 'ratio'}
                    aria-controls={VIDEO_OPTION_MENU_IDS.ratio}
                  >
                    <span>{ratio}</span>
                    <Icon name="chevronDown" size={12} />
                  </button>
                  <VideoOptionMenuPortal
                    menuId={VIDEO_OPTION_MENU_IDS.ratio}
                    open={openVideoOption === 'ratio'}
                    anchorEl={videoOptionTriggerRefs.current.ratio}
                    label={t('video.ratio')}
                    onClose={closeVideoOption}
                    options={selectedSpecForMode.ratios.map((value: string) => ({
                      key: value,
                      label: value,
                      selected: ratio === value,
                      onSelect: () => {
                        setRatio(value);
                        closeVideoOption();
                      },
                    }))}
                  />
                </div>
                {ratio === 'Auto' && <span className="video-helper">{t('video.ratioAutoHelper')}</span>}
              </label>

              <label className="video-field">
                <span className="video-label">{t('video.resolution')}</span>
                <div className={`custom-select image-option-select${openVideoOption === 'resolution' ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="custom-select-trigger image-option-trigger"
                    ref={(node) => {
                      videoOptionTriggerRefs.current.resolution = node;
                    }}
                    onClick={() => toggleVideoOption('resolution')}
                    aria-label={t('video.resolution')}
                    aria-haspopup="listbox"
                    aria-expanded={openVideoOption === 'resolution'}
                    aria-controls={VIDEO_OPTION_MENU_IDS.resolution}
                  >
                    <span>{resolution}</span>
                    <Icon name="chevronDown" size={12} />
                  </button>
                  <VideoOptionMenuPortal
                    menuId={VIDEO_OPTION_MENU_IDS.resolution}
                    open={openVideoOption === 'resolution'}
                    anchorEl={videoOptionTriggerRefs.current.resolution}
                    label={t('video.resolution')}
                    onClose={closeVideoOption}
                    options={selectedSpecForMode.resolutions.map((value: string) => ({
                      key: value,
                      label: value,
                      selected: resolution === value,
                      onSelect: () => {
                        setResolution(value);
                        closeVideoOption();
                      },
                    }))}
                  />
                </div>
              </label>

              {selectedSpecForMode.duration.kind === 'options' && (
                <label className="video-field">
                  <span className="video-label">{t('video.duration')}</span>
                  <div className={`custom-select image-option-select${openVideoOption === 'duration' ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="custom-select-trigger image-option-trigger"
                      ref={(node) => {
                        videoOptionTriggerRefs.current.duration = node;
                      }}
                      onClick={() => toggleVideoOption('duration')}
                      aria-label={t('video.duration')}
                      aria-haspopup="listbox"
                      aria-expanded={openVideoOption === 'duration'}
                      aria-controls={VIDEO_OPTION_MENU_IDS.duration}
                    >
                      <span>{`${durationSeconds} s`}</span>
                      <Icon name="chevronDown" size={12} />
                    </button>
                    <VideoOptionMenuPortal
                      menuId={VIDEO_OPTION_MENU_IDS.duration}
                      open={openVideoOption === 'duration'}
                      anchorEl={videoOptionTriggerRefs.current.duration}
                      label={t('video.duration')}
                      onClose={closeVideoOption}
                      options={selectedSpecForMode.duration.values.map((value: number) => ({
                        key: String(value),
                        label: `${value} s`,
                        selected: durationSeconds === value,
                        onSelect: () => {
                          setDurationSeconds(value);
                          closeVideoOption();
                        },
                      }))}
                    />
                  </div>
                </label>
              )}

              {selectedSpecForMode.duration.kind === 'range' && (
                <label className="video-field">
                  <span className="video-label">{t('video.duration')}</span>
                  <input
                    className="video-select"
                    type="number"
                    min={selectedSpecForMode.duration.min}
                    max={selectedSpecForMode.duration.max}
                    value={durationSeconds}
                    onChange={(event) => setDurationSeconds(Number(event.target.value))}
                  />
                  <span className="video-helper">
                    {selectedSpecForMode.duration.min}-{selectedSpecForMode.duration.max} s
                  </span>
                </label>
              )}

              {selectedSpecForMode.duration.kind === 'input-video' && (
                <div className="video-field">
                  <span className="video-label">{t('video.duration')}</span>
                  <span className="video-helper">
                    {fileDetailsByField.mainVideo?.[0]?.durationSeconds
                      ? t('video.durationInputValue', {
                          value: formatDuration(fileDetailsByField.mainVideo[0].durationSeconds),
                        })
                      : t('video.durationInput')}
                  </span>
                </div>
              )}

              {selectedSpecForMode.duration.kind === 'orientation-range' && (
                <>
                  <label className="video-field">
                    <span className="video-label">{t('video.orientation')}</span>
                    <div className={`custom-select image-option-select${openVideoOption === 'orientation' ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="custom-select-trigger image-option-trigger"
                        ref={(node) => {
                          videoOptionTriggerRefs.current.orientation = node;
                        }}
                        onClick={() => toggleVideoOption('orientation')}
                        aria-label={t('video.orientation')}
                        aria-haspopup="listbox"
                        aria-expanded={openVideoOption === 'orientation'}
                        aria-controls={VIDEO_OPTION_MENU_IDS.orientation}
                      >
                        <span>
                          {orientation === 'auto'
                            ? t('video.orientationAuto')
                            : orientation === 'video'
                              ? t('video.orientationVideo')
                              : t('video.orientationImage')}
                        </span>
                        <Icon name="chevronDown" size={12} />
                      </button>
                      <VideoOptionMenuPortal
                        menuId={VIDEO_OPTION_MENU_IDS.orientation}
                        open={openVideoOption === 'orientation'}
                        anchorEl={videoOptionTriggerRefs.current.orientation}
                        label={t('video.orientation')}
                        onClose={closeVideoOption}
                        options={[
                          {
                            key: 'auto',
                            label: t('video.orientationAuto'),
                            selected: orientation === 'auto',
                            onSelect: () => {
                              setOrientation('auto');
                              closeVideoOption();
                            },
                          },
                          {
                            key: 'video',
                            label: t('video.orientationVideo'),
                            selected: orientation === 'video',
                            onSelect: () => {
                              setOrientation('video');
                              closeVideoOption();
                            },
                          },
                          {
                            key: 'image',
                            label: t('video.orientationImage'),
                            selected: orientation === 'image',
                            onSelect: () => {
                              setOrientation('image');
                              closeVideoOption();
                            },
                          },
                        ]}
                      />
                    </div>
                  </label>
                  <label className="video-field">
                    <span className="video-label">{t('video.duration')}</span>
                    <input
                      className="video-select"
                      type="number"
                      min={durationLimit.min}
                      max={durationLimit.max}
                      value={durationSeconds}
                      onChange={(event) => setDurationSeconds(Number(event.target.value))}
                    />
                    <span className="video-helper">
                      {durationLimit.min}-{durationLimit.max} s
                    </span>
                  </label>
                </>
              )}
            </div>

            {selectedSpecForMode.contentModes && (
              <label className="video-field">
                <span className="video-label">{t('video.contentMode')}</span>
                <div className={`custom-select image-option-select${openVideoOption === 'contentMode' ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="custom-select-trigger image-option-trigger"
                    ref={(node) => {
                      videoOptionTriggerRefs.current.contentMode = node;
                    }}
                    onClick={() => toggleVideoOption('contentMode')}
                    aria-label={t('video.contentMode')}
                    aria-haspopup="listbox"
                    aria-expanded={openVideoOption === 'contentMode'}
                    aria-controls={VIDEO_OPTION_MENU_IDS.contentMode}
                  >
                    <span>{t(CONTENT_MODE_KEYS[contentMode]!)}</span>
                    <Icon name="chevronDown" size={12} />
                  </button>
                  <VideoOptionMenuPortal
                    menuId={VIDEO_OPTION_MENU_IDS.contentMode}
                    open={openVideoOption === 'contentMode'}
                    anchorEl={videoOptionTriggerRefs.current.contentMode}
                    label={t('video.contentMode')}
                    onClose={closeVideoOption}
                    options={selectedSpecForMode.contentModes.map((value: VideoContentMode) => ({
                      key: value,
                      label: t(CONTENT_MODE_KEYS[value]!),
                      selected: contentMode === value,
                      onSelect: () => {
                        setContentMode(value);
                        closeVideoOption();
                      },
                    }))}
                  />
                </div>
              </label>
            )}

            {audioConfig && (
              <div className="video-audio-card">
                {saveOriginalAudioOnly ? (
                  <div className="video-field">
                    <span className="video-label">{t('video.audioSourceMode')}</span>
                    <span className="video-helper">{t('video.audioSourcePreserve')}</span>
                  </div>
                ) : (
                  <>
                    <label className="model-filter-toggle">
                      <input
                        type="checkbox"
                        checked={audioEnabled}
                        onChange={(event) => setAudioEnabled(event.target.checked)}
                        aria-label={t('video.audioGeneration')}
                      />
                      <span>{t('video.audioGeneration')}</span>
                    </label>
                    <p className="video-helper">
                      {audioConfig.languages
                        ? t('video.audioLanguages', {
                            languages:
                             audioConfig.languages === 'Multi'
                                ? t('video.audioLanguagesMulti')
                                : audioConfig.languages.join(', '),
                          })
                        : t('video.audioLanguagesUnavailable')}
                    </p>
                    {audioEnabled && audioConfig.supportsSourceAudioMode && (
                      <label className="video-field">
                        <span className="video-label">{t('video.audioSourceMode')}</span>
                        <div className={`custom-select image-option-select${openVideoOption === 'audioSourceMode' ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            className="custom-select-trigger image-option-trigger"
                            ref={(node) => {
                              videoOptionTriggerRefs.current.audioSourceMode = node;
                            }}
                            onClick={() => toggleVideoOption('audioSourceMode')}
                            aria-label={t('video.audioSourceMode')}
                            aria-haspopup="listbox"
                            aria-expanded={openVideoOption === 'audioSourceMode'}
                            aria-controls={VIDEO_OPTION_MENU_IDS.audioSourceMode}
                          >
                            <span>{audioSourceMode === 'preserve' ? t('video.audioSourcePreserve') : t('video.audioSourceChange')}</span>
                            <Icon name="chevronDown" size={12} />
                          </button>
                          <VideoOptionMenuPortal
                            menuId={VIDEO_OPTION_MENU_IDS.audioSourceMode}
                            open={openVideoOption === 'audioSourceMode'}
                            anchorEl={videoOptionTriggerRefs.current.audioSourceMode}
                            label={t('video.audioSourceMode')}
                            onClose={closeVideoOption}
                            options={[
                              {
                                key: 'preserve',
                                label: t('video.audioSourcePreserve'),
                                selected: audioSourceMode === 'preserve',
                                onSelect: () => {
                                  setAudioSourceMode('preserve');
                                  closeVideoOption();
                                },
                              },
                              {
                                key: 'change',
                                label: t('video.audioSourceChange'),
                                selected: audioSourceMode === 'change',
                                onSelect: () => {
                                  setAudioSourceMode('change');
                                  closeVideoOption();
                                },
                              },
                            ]}
                          />
                        </div>
                      </label>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="video-upload-grid">
              {selectedSpecForMode.libraryVideo && (
                <div className="video-upload-card">
                  <label className="video-field" htmlFor="video-library-select">
                    <span className="video-label">
                      {t('video.fieldLibraryVideo')}
                      {selectedSpecForMode.libraryVideo.required ? ' *' : ''}
                    </span>
                    <div className={`custom-select image-option-select${openVideoOption === 'libraryVideo' ? ' is-open' : ''}`}>
                      <button
                        id="video-library-select"
                        type="button"
                        className="custom-select-trigger image-option-trigger"
                        ref={(node) => {
                          videoOptionTriggerRefs.current.libraryVideo = node;
                        }}
                        aria-required={selectedSpecForMode.libraryVideo.required}
                        aria-invalid={(fieldErrors.libraryVideo?.length ?? 0) > 0}
                        aria-describedby="video-library-helper video-library-errors"
                        aria-label={t('video.fieldLibraryVideo')}
                        aria-haspopup="listbox"
                        aria-expanded={openVideoOption === 'libraryVideo'}
                        aria-controls={VIDEO_OPTION_MENU_IDS.libraryVideo}
                        onClick={() => toggleVideoOption('libraryVideo')}
                      >
                        <span>
                          {libraryVideo
                            ? `${libraryVideo.name ?? libraryVideo.assetId} · ${formatDuration(libraryVideo.durationSeconds ?? 0)} · ${
                                eligibleVeoLibraryAssets.find((asset) => asset.assetId === libraryVideo.assetId)?.originDisplayName
                                ?? libraryVideo.originModelId
                                ?? t('video.libraryVideoOriginUnknown')
                              }`
                            : t('video.libraryVideoPlaceholder')}
                        </span>
                        <Icon name="chevronDown" size={12} />
                      </button>
                      <VideoOptionMenuPortal
                        menuId={VIDEO_OPTION_MENU_IDS.libraryVideo}
                        open={openVideoOption === 'libraryVideo'}
                        anchorEl={videoOptionTriggerRefs.current.libraryVideo}
                        label={t('video.fieldLibraryVideo')}
                        onClose={closeVideoOption}
                        options={[
                          {
                            key: '',
                            label: t('video.libraryVideoPlaceholder'),
                            selected: !libraryVideo,
                            onSelect: () => {
                              setLibraryVideo(null);
                              setFieldErrors((prev) => {
                                if (!prev.libraryVideo) return prev;
                                const next = { ...prev };
                                delete next.libraryVideo;
                                return next;
                              });
                              closeVideoOption();
                            },
                          },
                          ...eligibleVeoLibraryAssets.map((asset) => ({
                            key: asset.assetId,
                            label: `${asset.displayName} · ${formatDuration(asset.durationSeconds ?? 0)} · ${asset.originDisplayName}`,
                            selected: libraryVideo?.assetId === asset.assetId,
                            onSelect: () => {
                              setLibraryVideo(asset);
                              setFieldErrors((prev) => {
                                if (!prev.libraryVideo) return prev;
                                const next = { ...prev };
                                delete next.libraryVideo;
                                return next;
                              });
                              closeVideoOption();
                            },
                          })),
                        ]}
                      />
                    </div>
                  </label>
                  <span id="video-library-helper" className="video-helper">
                    {libraryVideoMetadataPending
                      ? t('video.libraryVideoLoading')
                      : eligibleVeoLibraryAssets.length > 0
                      ? t('video.libraryVideoHelper')
                      : t('video.libraryVideoEmpty')}
                  </span>
                  {libraryVideo && (
                    <div className="video-library-selection">
                      {libraryVideo.previewUrl && (
                        <video
                          className="video-library-preview"
                          src={libraryVideo.previewUrl}
                          controls
                          preload="metadata"
                          aria-label={t('video.libraryVideoPreviewLabel', {
                            name: libraryVideo.name ?? libraryVideo.assetId,
                          })}
                        />
                      )}
                      <dl className="video-library-meta">
                        <div>
                          <dt>{t('video.libraryVideoName')}</dt>
                          <dd>{libraryVideo.name ?? libraryVideo.assetId}</dd>
                        </div>
                        <div>
                          <dt>{t('video.libraryVideoDuration')}</dt>
                          <dd>{formatDuration(libraryVideo.durationSeconds ?? 0)}</dd>
                        </div>
                        <div>
                          <dt>{t('video.libraryVideoOrigin')}</dt>
                          <dd>
                            {eligibleVeoLibraryAssets.find((asset) => asset.assetId === libraryVideo.assetId)?.originDisplayName ??
                              libraryVideo.originModelId ??
                              t('video.libraryVideoOriginUnknown')}
                          </dd>
                        </div>
                      </dl>
                      <button type="button" className="btn btn-ghost video-clear-btn" onClick={() => clearLibraryVideo()}>
                        <Icon name="close" size={14} />
                        {t('video.clearFiles')}
                      </button>
                    </div>
                  )}
                  <div id="video-library-errors">
                    {(fieldErrors.libraryVideo ?? []).map((message, index) => (
                      <p key={`library-video-error-${index}`} className="image-file-error">
                        {message}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              {selectedFields.map((field) => {
                const upload = selectedSpecForMode.uploads[field];
                if (!upload) return null;
                const files = filesByField[field] ?? [];
                const details = fileDetailsByField[field] ?? [];
                return (
                  <label key={field} className="video-upload-card">
                    <span className="video-label">
                      {t(UPLOAD_FIELD_KEYS[field]!)}
                      {upload.required ? ' *' : ''}
                    </span>
                    <input
                      type="file"
                      accept={buildAccept(upload)}
                      multiple={(upload.maxFiles ?? 1) > 1}
                      required={upload.required}
                      aria-required={upload.required}
                      onChange={(event) => {
                        void handleFilesSelected(field, event.target.files, upload.maxFiles ?? 1);
                        event.target.value = '';
                      }}
                    />
                    <span className="video-helper">{buildUploadHelper(t, upload)}</span>
                    {generatedLibrary.items.some((item) => uploadKinds(upload).includes(item.kind)) && (
                      <button
                        type="button"
                        className="btn btn-ghost lib-trigger"
                        onClick={(event) => {
                          event.preventDefault();
                          setLibraryField(field);
                        }}
                      >
                        <Icon name="folder" size={16} />
                        {t('library.fromLibrary')}
                      </button>
                    )}
                    {files.length > 0 && (
                      <>
                        <ul className="video-file-list">
                          {details.map((file) => (
                            <li key={`${field}-${file.name}-${file.size}`}>
                              <span>{file.name}</span>
                              <span>
                                {formatBytes(file.size)}
                                {file.durationSeconds ? ` · ${formatDuration(file.durationSeconds)}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <button type="button" className="btn btn-ghost video-clear-btn" onClick={() => clearField(field)}>
                          <Icon name="close" size={14} />
                          {t('video.clearFiles')}
                        </button>
                      </>
                    )}
                    {(fieldErrors[field] ?? []).map((message, index) => (
                      <p key={`${field}-error-${index}`} className="image-file-error">
                        {message}
                      </p>
                    ))}
                  </label>
                );
              })}
            </div>

            {libraryField && selectedSpecForMode?.uploads[libraryField] && (
              <LibraryPickerModal
                open
                onClose={() => setLibraryField(null)}
                library={generatedLibrary}
                kinds={uploadKinds(selectedSpecForMode.uploads[libraryField]!)}
                maxSelect={Math.max(
                  1,
                  (selectedSpecForMode.uploads[libraryField]!.maxFiles ?? 1) -
                    (filesByField[libraryField]?.length ?? 0),
                )}
                onPick={(picked) =>
                  handleFilesSelected(
                    libraryField,
                    picked,
                    selectedSpecForMode.uploads[libraryField]!.maxFiles ?? 1,
                  )
                }
              />
            )}

            {(fieldErrors.totalFiles ?? []).map((message, index) => (
              <p key={`total-files-error-${index}`} className="image-file-error">
                {message}
              </p>
            ))}

            {selectedSpecForMode.howToUse && (
              <div className="video-howto">
                <p className="video-howto-title">{t('video.howToUse')}</p>
                <p className="video-howto-copy">{selectedSpecForMode.howToUse}</p>
              </div>
            )}
          </>
        )}

        <div className="media-input-row">
          <div className={`video-prompt-wrap${promptTooLong ? ' is-over-limit' : ''}`}>
            <textarea
              className="composer-input"
              placeholder={t('composer.videoPlaceholder')}
              value={prompt}
              rows={3}
              onChange={(event) => setPrompt(event.target.value)}
            />
            {promptTooLong && (
              <p className="video-over-limit-message">{t('video.validationPromptMax', { max: String(promptMax) })}</p>
            )}
            {(promptTooLong ? [] : fieldErrors.prompt ?? []).map((message, index) => (
              <p key={`prompt-error-${index}`} className="image-file-error">
                {message}
              </p>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-primary media-generate-btn"
            disabled={disabled}
            onClick={() => void handleGenerate()}
          >
            <Icon name="send" size={18} />
            {uploading ? t('job.uploading') : t('composer.generate')}
          </button>
        </div>
      </div>

      <div className="jobs-section">
        <VideoJobGrid
          jobs={sectionJobs}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onRegenerate={handleRegenerate}
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDeleteJob}
          onCancel={handleCancelJob}
        />
      </div>

      <ModelPicker
        open={pickerAnchor !== null}
        onClose={() => setPickerAnchor(null)}
        models={modeModels}
        section="video"
        selectedModelChoice={selectedModelId ?? ''}
        resolvedModelId={selectedModelId}
        anchor={pickerAnchor}
        onSelect={(id) => {
          setSelectedModelId(id);
          prefs.set(VIDEO_MODEL_PREF_KEY, id);
        }}
      />
    </div>
  );
}
